import { call, delay, put, select } from 'redux-saga/effects';
import { fetchRunDataFailure, fetchRunDataSuccess } from '../actions';
import { ApiError } from 'store/api/types';
import { FetchViewRunDataRequestAction } from '../types';
import { checkResponse } from 'store/utils';

import { GetRunDataResponse, getViewRunData } from '../api';
import { turnMessageOn } from 'store/app/actions';
import { MessageData, ProfilePicture } from 'store/app/types';
import { PICTURE_DELAY_TIME } from 'store/app/constants';
import { VatRunData } from '../models';
import { getProfilePictures } from 'store/app/selectors';
import { fetchProfilePictureRequest } from 'store/account/actions';
import { logoutRequest } from 'store/auth/actions';
import { push } from 'store/navigation/actions';
import routes from 'routes';
import { renderErrorMessage } from 'utils/render-error-message';

const getValidHmrcPeriodKey = (vatRunData: VatRunData): string | undefined => {
  const directPeriodKey = vatRunData.hmrcPeriodKey?.trim();
  if (directPeriodKey && directPeriodKey.toLowerCase() !== 'none') {
    return directPeriodKey;
  }
  const obligationPeriodKey = vatRunData.obligation
    ?.map((obligation) => obligation?.periodKey?.trim())
    .find((periodKey) => periodKey && periodKey.toLowerCase() !== 'none');
  return obligationPeriodKey || undefined;
};

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* fetchViewRunDataSaga(action: FetchViewRunDataRequestAction) {
  try {
    let err: ApiError | null;
    const { viewRunRequestData } = action.payload;

    const response: GetRunDataResponse = yield call(
      getViewRunData,
      viewRunRequestData
    );

    err = checkResponse(response?.responseMessage);
    if (err) throw err;

    const vatRunData: VatRunData = response.data;

    ///Get pics
    if (vatRunData) {
      vatRunData.hmrcPeriodKey = getValidHmrcPeriodKey(vatRunData);
      let pp: ProfilePicture[] = yield select(getProfilePictures);
      const requestedUserUuids = new Set<string>();
      const activityNames = ['staff', 'clients'];

      for (let b = 0; b < activityNames.length; b++) {
        const currentMembers = vatRunData[activityNames[b]] || [];
        let newMembers = [...currentMembers];
        for (let i = 0; i < currentMembers.length; i++) {
          const user = currentMembers[i];
          if (user.hasProfilePicture && !user.profilePicture) {
            const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
            if (ppIdx > -1) {
              const profilePicture = pp[ppIdx].profilePicture;
              newMembers[i] = { ...newMembers[i], profilePicture };
            } else if (!requestedUserUuids.has(user.userUuid)) {
              requestedUserUuids.add(user.userUuid);
              yield put(fetchProfilePictureRequest(user.userUuid));
              yield delay(PICTURE_DELAY_TIME);
              pp = yield select(getProfilePictures);
              const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
              if (ppIdx > -1) {
                const profilePicture = pp[ppIdx].profilePicture;
                newMembers[i] = { ...newMembers[i], profilePicture };
              }
            }
          }
        }

        vatRunData[activityNames[b]] = newMembers;
      }
    }

    yield put(fetchRunDataSuccess(vatRunData));
    //yield put(push(routes.vat.report));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(fetchRunDataFailure());
    // Avoid blank report shell (no vatRunData) after a failed open.
    yield put(push(routes.main));
  }
}
